/*
 * ATBT product page behaviour for sections/atbt-product-main.liquid (root: [data-atbt-pdp]).
 *
 * - Variant logic for any product (0-3 options, single-variant, sold-out variants, combinations
 *   that do not exist). The iPhone model option starts unselected unless the URL had ?variant=.
 * - Option UIs: swatch / button pills (price per pill when it varies), selects, the iPhone model
 *   select (optgroups) with the "Select your iPhone" trigger + model search dialog.
 * - Price, compare-at price, availability, CTA + sticky bar states, ?variant= sync, variant media.
 * - Gallery: thumbs, arrow keys, swipe, video / external video, close-up (zoom) dialog.
 * - Add to bag via /cart/add.js with readable errors, then the bag drawer (/cart.js, line
 *   quantity changes via /cart/change.js, View bag link, Checkout posting to /cart).
 * - Share (Web Share API, copy-link dialog fallback), model help and size guide dialogs.
 * - Delivery / reviews shortcuts open the info tabs of sections/atbt-product-info.liquid.
 *
 * Outside the section (e.g. the info tabs) these hooks work too:
 *   [data-dialog="fit"]          model help on iPhone products, otherwise the size guide
 *   [data-pdp-open="share|model-help|size-guide|fit|model|bag"]
 *   [data-photo="2"]             close-up of product.media index 2 (0-based)
 *   [data-photo-id="123"]        close-up of the media with that id
 * Events on document: atbt:pdp:open-panel {panel} (cancelable; call preventDefault() to handle the
 * tab switch yourself), atbt:cart:added {items, variantId}, atbt:pdp:variant {variant}.
 * API: window.ATBT.pdp = { open(name, opener), openPanel(name), openZoom(index), openBag() }.
 * Plain ES2018, no dependencies. Safe with theme-editor reloads.
 */
(function () {
  'use strict';

  if (window.AtbtPdp && typeof window.AtbtPdp.boot === 'function') {
    window.AtbtPdp.boot(document);
    return;
  }

  var doc = document;
  var instances = [];
  var ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  var reducedQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  /* --------------------------------------------------------------- helpers */

  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return ESCAPES[c];
    });
  }

  function fill(template, map) {
    var out = String(template == null ? '' : template);
    Object.keys(map).forEach(function (key) {
      out = out.split('[' + key + ']').join(String(map[key]));
    });
    return out;
  }

  function reduced() {
    return !!(reducedQuery && reducedQuery.matches);
  }

  function isEl(node) {
    return node instanceof Element;
  }

  function setText(el, text) {
    if (!el) return;
    var value = String(text == null ? '' : text);
    if (el.textContent !== value) el.textContent = value;
  }

  function focusEl(el) {
    if (!el || typeof el.focus !== 'function') return;
    try {
      el.focus({ preventScroll: true });
    } catch (error) {
      el.focus();
    }
  }

  function decodeEntities(text) {
    if (String(text).indexOf('&') === -1) return String(text);
    var area = doc.createElement('textarea');
    area.innerHTML = text;
    return area.value;
  }

  function icon(name) {
    return '<svg aria-hidden="true" focusable="false"><use href="#pdp-i-' + name + '"/></svg>';
  }

  /** Normalise an option value for display ("IPhone 15" -> "iPhone 15", "pink" -> "Pink"). */
  function displayValue(value, isModel) {
    if (value == null) return '';
    var raw = String(value);
    var out = raw.replace(/iphone/gi, 'iPhone');
    if (!isModel && raw === raw.toLowerCase()) out = out.charAt(0).toUpperCase() + out.slice(1);
    return out;
  }

  function readJSON(root, name) {
    var el = root.querySelector('script[data-pdp="' + name + '"]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  }

  function requestJSON(url, options) {
    var settings = Object.assign(
      {
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      },
      options || {}
    );
    return fetch(url, settings).then(function (response) {
      return response.text().then(function (text) {
        var data = null;
        try {
          data = text ? JSON.parse(text) : null;
        } catch (error) {
          data = null;
        }
        if (!response.ok) {
          var failure = new Error('Request failed: ' + response.status);
          failure.userMessage = (data && (data.description || data.message)) || '';
          if (typeof failure.userMessage !== 'string') failure.userMessage = '';
          throw failure;
        }
        return data;
      });
    });
  }

  /** Update the header bag count: through ATBT when its header is loaded, otherwise Dawn's #cart-icon-bubble. */
  function refreshHeaderCart() {
    if (window.ATBT && typeof window.ATBT.refreshCartCount === 'function') {
      window.ATBT.refreshCartCount().catch(function () {});
      return;
    }
    var icon = document.getElementById('cart-icon-bubble');
    if (!icon) return;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    requestJSON(root + '?sections=cart-icon-bubble')
      .then(function (sections) {
        var html = sections && sections['cart-icon-bubble'];
        if (!html) return;
        var section = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
        if (section) icon.innerHTML = section.innerHTML;
      })
      .catch(function () {});
  }

  function withWidth(url, width) {
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /* ----------------------------------------------------------- scroll lock */

  var lockCount = 0;
  var previousOverflow = '';

  function lockScroll(dialog) {
    if (dialog.__pdpLocked) return;
    dialog.__pdpLocked = true;
    if (lockCount === 0) {
      previousOverflow = doc.body.style.overflow;
      doc.body.style.overflow = 'hidden';
    }
    lockCount += 1;
  }

  function unlockScroll(dialog) {
    if (!dialog.__pdpLocked) return;
    dialog.__pdpLocked = false;
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) doc.body.style.overflow = previousOverflow;
  }

  function showModal(dialog) {
    if (dialog.open) return;
    if (typeof dialog.showModal === 'function') {
      try {
        dialog.showModal();
        return;
      } catch (error) {
        /* fall through */
      }
    }
    dialog.setAttribute('open', '');
  }

  function closeModal(dialog) {
    if (!dialog || !dialog.open) return;
    if (typeof dialog.close === 'function') dialog.close();
    else {
      dialog.removeAttribute('open');
      dialog.dispatchEvent(new Event('close'));
    }
  }

  function outsideRect(dialog, event) {
    var rect = dialog.getBoundingClientRect();
    return (
      event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom
    );
  }

  /* ------------------------------------------------------------- instance */

  function Pdp(root) {
    this.root = root;
    root.__atbtPdp = this;
    this.data = readJSON(root, 'product-json') || {};
    var mediaData = readJSON(root, 'media-json') || {};
    this.media = Array.isArray(mediaData.media) ? mediaData.media : [];
    this.mediaIndex = Number(mediaData.active) || 0;
    this.shownImage = this.mediaIndex;
    this.ms = readJSON(root, 'model-json') || {};
    this.t = this.data.strings || {};
    this.variants = Array.isArray(this.data.variants) ? this.data.variants : [];
    this.routes = this.data.routes || {};
    this.qty = 1;
    this.busy = false;
    this.zoomPos = 0;
    this.dialogReturn = null;
    this.modelReturn = null;
    this.touchStart = null;
    this.suppressPhotoClick = 0;
    this.toastTimer = 0;
    this.pointerTarget = null;

    this.cache();
    this.readOptions();
    this.bind();
    this.observeSticky();
    this.update(false);
    this.prepareMedia();
    instances.push(this);
  }

  Pdp.prototype.q = function (name) {
    return this.root.querySelector('[data-pdp="' + name + '"]');
  };

  Pdp.prototype.cache = function () {
    var q = this.q.bind(this);
    var idInput = q('variant-id');
    var sticky = q('sticky');
    this.el = {
      idInput: idInput,
      form: q('form') || (idInput ? idInput.form : null),
      qty: q('qty'),
      minus: q('qty-minus'),
      plus: q('qty-plus'),
      add: q('add'),
      addLabel: q('add-label'),
      error: q('error'),
      price: q('price'),
      compare: q('compare'),
      compareAmount: q('compare-amount'),
      fitNote: q('fit-note'),
      availability: q('availability'),
      hero: q('hero'),
      mainPhoto: q('main-photo') || this.root.querySelector('.main-photo'),
      zoom: q('zoom'),
      thumbs: q('thumbs'),
      buyControls: q('buy-controls'),
      sticky: sticky,
      stickyAdd: q('sticky-add'),
      stickyModel: q('sticky-model'),
      stickyImage: q('sticky-image') || (sticky ? sticky.querySelector('img') : null),
      dialog: q('dialog'),
      dialogContent: q('dialog-content'),
      toast: q('toast'),
      modelDialog: q('model-dialog'),
      modelSearch: q('model-search'),
      modelResults: q('model-results'),
      modelStatus: q('model-status'),
      modelTrigger: q('model-trigger'),
      modelTriggerValue: q('model-trigger-value'),
      modelTriggerAction: q('model-trigger-action')
    };
  };

  Pdp.prototype.readOptions = function () {
    var self = this;
    this.groups = [];
    this.root.querySelectorAll('[data-pdp-option]').forEach(function (el) {
      self.groups.push({
        el: el,
        index: Number(el.getAttribute('data-pdp-option')),
        kind: el.getAttribute('data-kind') || 'pills',
        showPrice: el.hasAttribute('data-show-price'),
        buttons: Array.prototype.slice.call(el.querySelectorAll('[data-pdp-value]')),
        select: el.querySelector('select[data-pdp-select]'),
        current: el.querySelector('[data-pdp="option-current"]')
      });
    });
    this.modelGroup = null;
    for (var i = 0; i < this.groups.length; i += 1) {
      if (this.groups[i].kind === 'model') this.modelGroup = this.groups[i];
    }
    this.modelIndex = this.modelGroup ? this.modelGroup.index : -1;
    this.colorGroup = null;
    for (var j = 0; j < this.groups.length; j += 1) {
      if (this.groups[j].kind === 'color' && !this.colorGroup) this.colorGroup = this.groups[j];
    }

    var initialId = String(this.data.initialVariant);
    var initial = null;
    this.variants.forEach(function (variant) {
      if (String(variant.id) === initialId) initial = variant;
    });
    this.fallbackVariant = initial || this.variants[0] || null;
    this.values = this.fallbackVariant ? this.fallbackVariant.options.slice() : [];
    if (this.modelIndex > -1 && this.data.variantReady === false) this.values[this.modelIndex] = null;
  };

  /* --------------------------------------------------------- variant logic */

  Pdp.prototype.modelMissing = function () {
    return this.modelIndex > -1 && this.values[this.modelIndex] == null;
  };

  Pdp.prototype.findVariant = function (values) {
    for (var i = 0; i < values.length; i += 1) if (values[i] == null) return null;
    for (var v = 0; v < this.variants.length; v += 1) {
      var options = this.variants[v].options;
      var match = true;
      for (var o = 0; o < options.length; o += 1) {
        if (options[o] !== values[o]) {
          match = false;
          break;
        }
      }
      if (match) return this.variants[v];
    }
    return null;
  };

  /** A variant with `value` at `index` that agrees with the other chosen values. */
  Pdp.prototype.candidate = function (index, value, requireAvailable) {
    var values = this.values;
    for (var v = 0; v < this.variants.length; v += 1) {
      var variant = this.variants[v];
      if (variant.options[index] !== value) continue;
      if (requireAvailable && !variant.available) continue;
      var match = true;
      for (var j = 0; j < variant.options.length; j += 1) {
        if (j !== index && values[j] != null && variant.options[j] !== values[j]) {
          match = false;
          break;
        }
      }
      if (match) return variant;
    }
    return null;
  };

  Pdp.prototype.cheapest = function (index, value) {
    var best = null;
    this.variants.forEach(function (variant) {
      if (variant.options[index] === value && (!best || variant.price < best.price)) best = variant;
    });
    return best;
  };

  Pdp.prototype.selectValue = function (index, value) {
    var self = this;
    this.values[index] = value === '' || value == null ? null : value;
    var complete = this.values.every(function (v) {
      return v != null;
    });
    if (complete && !this.findVariant(this.values)) {
      // That combination does not exist: keep the new value and move the others to the closest
      // existing (preferably available) variant.
      var best = null;
      var bestScore = -1;
      this.variants.forEach(function (variant) {
        if (variant.options[index] !== value) return;
        var score = variant.available ? 1 : 0;
        variant.options.forEach(function (option, j) {
          if (j !== index && option === self.values[j]) score += 2;
        });
        if (score > bestScore) {
          best = variant;
          bestScore = score;
        }
      });
      if (best) {
        best.options.forEach(function (option, j) {
          if (j !== index) self.values[j] = option;
        });
      }
    }
    this.clearError();
    this.update(true);
  };

  Pdp.prototype.groupFor = function (index) {
    for (var i = 0; i < this.groups.length; i += 1) if (this.groups[i].index === index) return this.groups[i];
    return null;
  };

  /** The label shown on the control for this value (short size labels, normalised models). */
  Pdp.prototype.labelFor = function (group, value) {
    if (value == null) return '';
    if (group && group.buttons.length) {
      for (var i = 0; i < group.buttons.length; i += 1) {
        var button = group.buttons[i];
        if (button.getAttribute('data-pdp-value') === value) {
          var label = button.querySelector('.pill-label');
          if (label) return label.textContent.trim();
        }
      }
    }
    if (group && group.select) {
      var options = group.select.options;
      for (var o = 0; o < options.length; o += 1) {
        if (options[o].value === value) return this.optionLabel(options[o]);
      }
    }
    return displayValue(value, group && group.kind === 'model');
  };

  Pdp.prototype.optionLabel = function (option) {
    if (option.getAttribute('data-label') == null) {
      var suffix = ' — ' + (this.t.optionSoldOut || '');
      var text = option.textContent.trim();
      if (this.t.optionSoldOut && text.slice(-suffix.length) === suffix) text = text.slice(0, -suffix.length);
      option.setAttribute('data-label', text.trim());
    }
    return option.getAttribute('data-label');
  };

  /** Chosen values of the visible options, joined with " · ". */
  Pdp.prototype.summary = function (short) {
    var self = this;
    var parts = [];
    this.groups.forEach(function (group) {
      var value = self.values[group.index];
      if (value == null) return;
      if (short || group.kind === 'model') parts.push(self.labelFor(group, value));
      else parts.push(displayValue(value, false));
    });
    return parts.join(' · ');
  };

  Pdp.prototype.money = function (cents) {
    var out = '';
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        out = decodeEntities(String(window.Shopify.formatMoney(cents, this.data.moneyFormat)).replace(/<[^>]*>/g, ''));
      } else if (window.ATBT && typeof window.ATBT.formatMoney === 'function') {
        out = window.ATBT.formatMoney(cents, this.data.moneyFormat);
      }
    } catch (error) {
      out = '';
    }
    if (!out) out = '$' + (Number(cents) / 100).toFixed(2);
    // Match Liquid's money_without_trailing_zeros.
    return String(out).replace(/([.,])00(?![0-9])/, '');
  };

  Pdp.prototype.update = function (fromUser) {
    var self = this;
    var t = this.t;
    var ms = this.ms;
    var variant = this.findVariant(this.values);
    var changed = variant !== this.variant;
    this.variant = variant;
    var missing = this.modelMissing();
    var priced = variant || this.fallbackVariant;

    if (priced) {
      setText(this.el.price, this.money(priced.price));
      var onSale = priced.compare > priced.price;
      if (this.el.compare) this.el.compare.hidden = !onSale;
      if (onSale) setText(this.el.compareAmount, this.money(priced.compare));
    }

    if (this.el.idInput) this.el.idInput.value = variant ? String(variant.id) : '';

    var availability = '';
    if (!missing) availability = !variant ? t.unavailable : variant.available ? t.inStock : t.soldOut;
    setText(this.el.availability, availability);

    var note = this.summary(false);
    if (this.modelIndex > -1) note = missing ? ms.note || '' : fill(ms.selected || '[model]', { model: note });
    setText(this.el.fitNote, note);

    this.groups.forEach(function (group) {
      self.renderGroup(group);
    });
    this.renderModelTrigger();
    this.renderButtons();

    if (this.el.stickyModel) {
      setText(this.el.stickyModel, missing ? t.stickyChooseModel || ms.placeholder || '' : this.summary(true));
    }

    if (this.colorGroup) {
      var colour = this.values[this.colorGroup.index];
      if (colour != null) this.root.setAttribute('data-outfit', this.labelFor(this.colorGroup, colour));
    }

    var mediaIndex = variant && variant.media != null ? this.mediaIndexById(variant.media) : -1;
    if (mediaIndex > -1 && this.el.stickyImage && this.media[mediaIndex].thumb) {
      this.el.stickyImage.src = this.media[mediaIndex].thumb;
      this.el.stickyImage.removeAttribute('srcset');
    }

    if (fromUser) {
      this.updateUrl(variant);
      if (mediaIndex > -1 && changed) this.showMedia(mediaIndex);
      if (changed) {
        doc.dispatchEvent(new CustomEvent('atbt:pdp:variant', { detail: { variant: variant, sectionId: this.root.getAttribute('data-section-id') } }));
      }
    }
  };

  Pdp.prototype.renderGroup = function (group) {
    var self = this;
    var selected = this.values[group.index];

    group.buttons.forEach(function (button) {
      var value = button.getAttribute('data-pdp-value');
      var match = self.candidate(group.index, value, false);
      var available = !!self.candidate(group.index, value, true);
      button.setAttribute('aria-pressed', String(value === selected));
      button.classList.toggle('is-soldout', !available);
      button.classList.toggle('is-unavailable', !match);
      var status = button.querySelector('[data-pdp="pill-status"]');
      if (status) status.hidden = available;
      if (group.showPrice) {
        var priceEl = button.querySelector('[data-pdp="pill-price"]');
        var priced = match || self.cheapest(group.index, value);
        if (priceEl && priced) setText(priceEl, ' · ' + self.money(priced.price));
      }
    });

    if (group.select) {
      var suffix = this.t.optionSoldOut ? ' — ' + this.t.optionSoldOut : '';
      Array.prototype.forEach.call(group.select.options, function (option) {
        if (!option.value) return;
        var label = self.optionLabel(option);
        var available = !!self.candidate(group.index, option.value, true);
        if (group.kind === 'model') option.disabled = !available && option.value !== selected;
        setText(option, label + (available ? '' : suffix));
      });
      var wanted = selected == null ? '' : selected;
      if (group.select.value !== wanted) group.select.value = wanted;
      group.select.removeAttribute('aria-invalid');
    }

    if (group.current && selected != null) setText(group.current, this.labelFor(group, selected));
  };

  Pdp.prototype.renderModelTrigger = function () {
    if (!this.modelGroup) return;
    var value = this.values[this.modelIndex];
    setText(this.el.modelTriggerValue, value == null ? this.ms.placeholder || '' : this.labelFor(this.modelGroup, value));
    setText(this.el.modelTriggerAction, value == null ? this.ms.find || '' : this.ms.change || '');
  };

  Pdp.prototype.renderButtons = function () {
    var t = this.t;
    var variant = this.variant;
    var priced = variant || this.fallbackVariant;
    var total = priced ? this.money(priced.price * this.qty) : '';
    var label;
    var sticky;
    var disabled = false;

    if (this.busy) {
      label = t.adding || '';
      sticky = label;
    } else if (this.modelMissing()) {
      label = t.chooseModel || '';
      sticky = label + (total ? ' · ' + total : '');
    } else if (!variant) {
      label = t.unavailable || '';
      sticky = label;
      disabled = true;
    } else if (!variant.available) {
      label = t.soldOut || '';
      sticky = label;
      disabled = true;
    } else {
      sticky = (t.add || '') + ' · ' + total;
      label = this.data.ctaShowPrice === false ? t.add || '' : sticky;
    }

    setText(this.el.addLabel, label);
    if (this.el.add) {
      this.el.add.disabled = disabled;
      this.el.add.classList.toggle('is-loading', this.busy);
      if (this.busy) this.el.add.setAttribute('aria-busy', 'true');
      else this.el.add.removeAttribute('aria-busy');
    }
    if (this.el.stickyAdd) {
      setText(this.el.stickyAdd, sticky);
      this.el.stickyAdd.disabled = disabled;
      this.el.stickyAdd.classList.toggle('is-loading', this.busy);
    }
  };

  Pdp.prototype.updateUrl = function (variant) {
    if (window.Shopify && window.Shopify.designMode) return;
    try {
      var url = new URL(window.location.href);
      if (variant) url.searchParams.set('variant', String(variant.id));
      else url.searchParams.delete('variant');
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (error) {
      /* ignore (e.g. sandboxed previews) */
    }
  };

  /* -------------------------------------------------------------- quantity */

  Pdp.prototype.setQty = function (value) {
    var number = Math.floor(Number(value));
    if (!isFinite(number) || number < 1) number = 1;
    if (number > 99) number = 99;
    this.qty = number;
    if (this.el.qty && String(this.el.qty.value) !== String(number)) this.el.qty.value = number;
    if (this.el.minus) this.el.minus.disabled = number <= 1;
    if (this.el.plus) this.el.plus.disabled = number >= 99;
    this.renderButtons();
  };

  /* ----------------------------------------------------------------- media */

  Pdp.prototype.mediaIndexById = function (id) {
    for (var i = 0; i < this.media.length; i += 1) if (String(this.media[i].id) === String(id)) return i;
    return -1;
  };

  Pdp.prototype.isVideo = function (entry) {
    return !!entry && (entry.type === 'video' || entry.type === 'external_video');
  };

  Pdp.prototype.prepareMedia = function () {
    var self = this;
    var img = this.el.mainPhoto;
    if (img && img.tagName === 'IMG') {
      img.addEventListener('load', function () {
        if (reduced() || typeof img.animate !== 'function' || !self.animateNextLoad) return;
        self.animateNextLoad = false;
        img.animate([{ opacity: 0.4, transform: 'scale(1.015)' }, { opacity: 1, transform: 'scale(1)' }], {
          duration: 300,
          easing: 'ease-out'
        });
      });
    }
    var entry = this.media[this.mediaIndex];
    if (entry && entry.type === 'external_video' && this.el.hero) {
      var box = this.el.hero.querySelector('[data-pdp-embed="' + this.mediaIndex + '"]');
      if (box) this.ensureEmbed(box, false);
    }
  };

  Pdp.prototype.ensureEmbed = function (box, autoplay) {
    if (box.querySelector('iframe')) return;
    var src = box.getAttribute('data-embed-src') || '';
    if (!src) return;
    if (autoplay) src += (src.indexOf('?') === -1 ? '?' : '&') + 'autoplay=1';
    var frame = doc.createElement('iframe');
    frame.src = src;
    frame.title = box.getAttribute('data-embed-title') || '';
    frame.setAttribute('allow', 'autoplay; encrypted-media; fullscreen; picture-in-picture');
    frame.setAttribute('allowfullscreen', '');
    frame.setAttribute('loading', 'lazy');
    frame.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;border:0';
    box.appendChild(frame);
  };

  Pdp.prototype.showMedia = function (index, fromUser) {
    var self = this;
    var entry = this.media[index];
    if (!entry) return;
    this.mediaIndex = index;
    var hero = this.el.hero;
    if (!hero) return;
    var video = this.isVideo(entry);

    hero.querySelectorAll('video[data-pdp-video]').forEach(function (el) {
      var own = Number(el.getAttribute('data-pdp-video')) === index;
      if (!own && !el.paused) {
        try {
          el.pause();
        } catch (error) {
          /* ignore */
        }
      }
      el.hidden = !own;
    });
    hero.querySelectorAll('[data-pdp-embed]').forEach(function (box) {
      var own = Number(box.getAttribute('data-pdp-embed')) === index;
      box.hidden = !own;
      if (own) self.ensureEmbed(box, !!fromUser);
      else {
        var frame = box.querySelector('iframe');
        if (frame) frame.remove();
      }
    });

    var img = this.el.mainPhoto;
    if (img && img.tagName === 'IMG') {
      img.hidden = video;
      if (!video && this.shownImage !== index) {
        this.shownImage = index;
        this.animateNextLoad = true;
        if (entry.srcset) img.srcset = entry.srcset;
        else img.removeAttribute('srcset');
        img.src = entry.src;
        img.alt = entry.alt || '';
        if (entry.width && entry.height) {
          img.width = entry.width;
          img.height = entry.height;
        }
      }
    }
    if (this.el.zoom) this.el.zoom.hidden = video;

    if (this.el.thumbs) {
      var active = null;
      this.el.thumbs.querySelectorAll('[data-media]').forEach(function (button) {
        var own = Number(button.getAttribute('data-media')) === index;
        button.classList.toggle('active', own);
        button.setAttribute('aria-pressed', String(own));
        if (own) active = button;
      });
      if (active) this.revealThumb(active);
    }
  };

  /** Scroll a thumb into view inside its own scroller (never the page). */
  Pdp.prototype.revealThumb = function (button) {
    var box = this.el.thumbs;
    if (!box) return;
    var behavior = reduced() ? 'auto' : 'smooth';
    if (box.scrollHeight > box.clientHeight + 1) {
      var top = button.offsetTop - box.offsetTop;
      if (top < box.scrollTop || top + button.offsetHeight > box.scrollTop + box.clientHeight) {
        box.scrollTo({ top: Math.max(0, top - box.clientHeight / 2 + button.offsetHeight / 2), behavior: behavior });
      }
    }
    if (box.scrollWidth > box.clientWidth + 1) {
      var left = button.offsetLeft - box.offsetLeft;
      if (left < box.scrollLeft || left + button.offsetWidth > box.scrollLeft + box.clientWidth) {
        box.scrollTo({ left: Math.max(0, left - box.clientWidth / 2 + button.offsetWidth / 2), behavior: behavior });
      }
    }
  };

  Pdp.prototype.stepMedia = function (step) {
    if (!this.media.length) return;
    var next = (this.mediaIndex + step + this.media.length) % this.media.length;
    this.showMedia(next, true);
  };

  /* --------------------------------------------------------------- dialogs */

  Pdp.prototype.template = function (name) {
    var tpl = this.root.querySelector('template[data-pdp-tpl="' + name + '"]');
    return tpl && tpl.content ? tpl.content.cloneNode(true) : null;
  };

  Pdp.prototype.openDialog = function (type, content, opener) {
    var dialog = this.el.dialog;
    var box = this.el.dialogContent;
    if (!dialog || !box) return;
    dialog.className = 'pdp-dialog' + (type ? ' ' + type : '');
    box.innerHTML = '';
    if (typeof content === 'string') box.innerHTML = content;
    else if (content) box.appendChild(content);
    if (!dialog.open) {
      this.dialogReturn = isEl(opener) ? opener : doc.activeElement;
      showModal(dialog);
      lockScroll(dialog);
    }
  };

  Pdp.prototype.closeDialog = function (restoreFocus) {
    if (restoreFocus === false) this.dialogReturn = null;
    closeModal(this.el.dialog);
  };

  Pdp.prototype.onDialogClose = function () {
    var dialog = this.el.dialog;
    unlockScroll(dialog);
    var target = this.dialogReturn;
    this.dialogReturn = null;
    if (target && target !== doc.body && doc.contains(target)) focusEl(target);
  };

  Pdp.prototype.dialogType = function () {
    var dialog = this.el.dialog;
    if (!dialog || !dialog.open) return '';
    if (dialog.classList.contains('zoom-dialog')) return 'zoom';
    if (dialog.classList.contains('bag-dialog')) return 'bag';
    return 'other';
  };

  Pdp.prototype.open = function (name, opener) {
    switch (name) {
      case 'share':
        this.share(opener);
        break;
      case 'model':
        this.promptModel(opener, false);
        break;
      case 'model-help':
        this.openModelHelp(opener);
        break;
      case 'size-guide':
        this.openSizeGuide(opener);
        break;
      case 'fit':
        if (this.modelGroup) this.openModelHelp(opener);
        else this.openSizeGuide(opener);
        break;
      case 'bag':
        this.openBag(null, false, opener);
        break;
      case 'zoom':
        this.openZoom(this.mediaIndex, opener);
        break;
      default:
        break;
    }
  };

  Pdp.prototype.openModelHelp = function (opener) {
    var content = this.template('model-help');
    if (content) this.openDialog('', content, opener);
  };

  Pdp.prototype.openSizeGuide = function (opener) {
    var content = this.template('size-guide');
    if (content) this.openDialog('', content, opener);
  };

  /* ------------------------------------------------------------------ zoom */

  Pdp.prototype.zoomList = function () {
    var list = [];
    this.media.forEach(function (entry, index) {
      if (entry.type === 'image' || entry.type === 'model') list.push(index);
    });
    return list;
  };

  Pdp.prototype.openZoom = function (index, opener) {
    var list = this.zoomList();
    if (!list.length) return;
    var position = list.indexOf(Number(index));
    this.zoomPos = position > -1 ? position : 0;
    var t = this.t;
    var controls =
      list.length > 1
        ? '<div class="zoom-controls">' +
          '<button type="button" class="icon" data-pdp-action="zoom-prev">' + esc(t.zoomPrevious) + '</button>' +
          '<button type="button" class="icon" data-pdp-action="zoom-next">' + esc(t.zoomNext) + '</button>' +
          '</div>'
        : '';
    this.openDialog(
      'zoom-dialog',
      '<h2 id="dialog-title" class="dialog-title visually-hidden" data-pdp="zoom-title"></h2>' +
        '<img class="zoom-image" data-pdp="zoom-image" src="" alt="">' +
        controls,
      opener
    );
    this.renderZoom();
  };

  Pdp.prototype.renderZoom = function () {
    var list = this.zoomList();
    var index = list[this.zoomPos];
    var entry = this.media[index];
    var box = this.el.dialogContent;
    if (!entry || !box) return;
    var image = box.querySelector('[data-pdp="zoom-image"]');
    var title = box.querySelector('[data-pdp="zoom-title"]');
    setText(title, fill(this.t.zoomTitle || '[title], photo [n]', { title: this.data.title || '', n: index + 1 }));
    if (image) {
      image.src = entry.zoom || entry.src;
      image.alt = entry.alt || '';
      if (entry.width && entry.height) {
        image.width = entry.width;
        image.height = entry.height;
      }
    }
  };

  Pdp.prototype.stepZoom = function (step) {
    var list = this.zoomList();
    if (list.length < 2) return;
    this.zoomPos = (this.zoomPos + step + list.length) % list.length;
    this.renderZoom();
  };

  /* ------------------------------------------------------------ model picker */

  Pdp.prototype.promptModel = function (opener, withError) {
    if (this.el.modelDialog) {
      this.openModelDialog(opener);
      return;
    }
    var select = this.modelGroup ? this.modelGroup.select : null;
    if (withError) {
      this.showError(this.ms.error || this.t.errorUnavailable || '');
      if (select) select.setAttribute('aria-invalid', 'true');
    }
    if (select) {
      var picker = select.closest('.fit-picker') || select;
      picker.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'center' });
      focusEl(select);
    }
  };

  Pdp.prototype.openModelDialog = function (opener) {
    var dialog = this.el.modelDialog;
    if (!dialog || !this.modelGroup) return;
    if (this.el.modelSearch) this.el.modelSearch.value = '';
    this.renderModelResults();
    if (!dialog.open) {
      this.modelReturn = this.el.modelTrigger || (isEl(opener) ? opener : doc.activeElement);
      showModal(dialog);
      lockScroll(dialog);
    }
    focusEl(this.el.modelSearch);
  };

  Pdp.prototype.renderModelResults = function () {
    var self = this;
    var box = this.el.modelResults;
    var group = this.modelGroup;
    if (!box || !group || !group.select) return;
    var ms = this.ms;
    var query = this.el.modelSearch ? this.el.modelSearch.value.toLowerCase().trim().replace(/\s+/g, ' ') : '';
    var words = query ? query.split(' ') : [];
    var selected = this.values[this.modelIndex];
    var matches = Array.prototype.filter.call(group.select.options, function (option) {
      if (!option.value) return false;
      var label = self.optionLabel(option).toLowerCase();
      return words.every(function (word) {
        return label.indexOf(word) > -1;
      });
    });

    if (!matches.length) {
      box.innerHTML = '<p class="model-no-results">' + esc(ms.noResults) + '</p>';
    } else {
      box.innerHTML = matches
        .map(function (option) {
          var available = !!self.candidate(self.modelIndex, option.value, true);
          var isSelected = option.value === selected;
          var tag = !available ? self.t.optionSoldOut : isSelected ? ms.selectedTag : ms.choose;
          return (
            '<button type="button" class="model-result" data-pdp-model="' + esc(option.value) + '"' +
            ' aria-pressed="' + isSelected + '"' + (available ? '' : ' disabled') + '>' +
            '<span>' + esc(self.optionLabel(option)) + '</span><span>' + esc(tag) + '</span></button>'
          );
        })
        .join('');
    }
    setText(this.el.modelStatus, matches.length ? fill(ms.count || '[count]', { count: matches.length }) : ms.noResults || '');
  };

  Pdp.prototype.chooseModel = function (value) {
    var dialog = this.el.modelDialog;
    this.modelReturn = this.el.modelTrigger || this.modelReturn;
    this.selectValue(this.modelIndex, value);
    closeModal(dialog);
  };

  Pdp.prototype.onModelDialogClose = function () {
    unlockScroll(this.el.modelDialog);
    var target = this.modelReturn;
    this.modelReturn = null;
    if (target && doc.contains(target)) focusEl(target);
  };

  /* ---------------------------------------------------------------- errors */

  Pdp.prototype.showError = function (message, source) {
    setText(this.el.error, message);
    if (source === 'sticky' || !this.inViewport(this.el.error)) this.toast(message);
  };

  Pdp.prototype.clearError = function () {
    setText(this.el.error, '');
  };

  Pdp.prototype.inViewport = function (el) {
    if (!el) return false;
    var rect = el.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < (window.innerHeight || doc.documentElement.clientHeight);
  };

  Pdp.prototype.toast = function (message) {
    var toast = this.el.toast;
    if (!toast || !message) return;
    setText(toast, message);
    toast.classList.add('show');
    window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(function () {
      toast.classList.remove('show');
    }, 2800);
  };

  /* ------------------------------------------------------------------- cart */

  Pdp.prototype.setBusy = function (busy) {
    this.busy = busy;
    this.renderButtons();
  };

  Pdp.prototype.addToBag = function (source) {
    var self = this;
    var t = this.t;
    if (this.busy) return;
    if (this.modelMissing()) {
      this.promptModel(source === 'sticky' ? this.el.stickyAdd : this.el.add, true);
      return;
    }
    var variant = this.variant;
    if (!variant) {
      this.showError(t.errorUnavailable, source);
      return;
    }
    if (!variant.available) {
      this.showError(t.errorSoldOut, source);
      return;
    }

    var body = this.el.form ? new FormData(this.el.form) : new FormData();
    body.set('id', String(variant.id));
    body.set('quantity', String(this.qty));
    var opener = source === 'sticky' ? this.el.stickyAdd : this.el.add;

    this.clearError();
    this.setBusy(true);
    requestJSON((this.routes.cartAdd || '/cart/add') + '.js', { method: 'POST', body: body })
      .then(function (item) {
        doc.dispatchEvent(
          new CustomEvent('atbt:cart:added', {
            detail: { items: item && Array.isArray(item.items) ? item.items : [item], variantId: variant.id }
          })
        );
        return self.fetchCart().then(
          function (cart) {
            self.openBag(cart, true, opener);
          },
          function () {
            self.toast(t.bagAdded);
          }
        );
      })
      .catch(function (error) {
        self.showError((error && error.userMessage) || t.errorGeneric, source);
      })
      .then(function () {
        self.setBusy(false);
      });
  };

  /** Read /cart.js, updating the header bag count through ATBT when it is loaded (else Dawn's cart icon). */
  Pdp.prototype.fetchCart = function () {
    var cartUrl = (this.routes.cart || '/cart') + '.js';
    if (window.ATBT && typeof window.ATBT.refreshCartCount === 'function') {
      return window.ATBT.refreshCartCount().catch(function () {
        return requestJSON(cartUrl);
      });
    }
    refreshHeaderCart();
    return requestJSON(cartUrl);
  };

  Pdp.prototype.openBag = function (cart, added, opener) {
    var self = this;
    if (!cart) {
      this.fetchCart().then(
        function (fresh) {
          self.openBag(fresh, added, opener);
        },
        function () {
          self.toast(self.t.bagError);
        }
      );
      return;
    }
    this.openDialog('bag-dialog', this.bagHTML(cart, added), opener);
  };

  Pdp.prototype.bagHTML = function (cart, added) {
    var self = this;
    var t = this.t;
    var items = Array.isArray(cart.items) ? cart.items : [];
    var count = Number(cart.item_count) || 0;
    var cartUrl = this.routes.cart || '/cart';
    var html = '';

    if (added) html += '<div class="bag-confirmation" role="status">' + icon('check') + esc(t.bagAdded) + '</div>';
    html += '<h2 id="dialog-title" class="dialog-title">' + esc(t.bagTitle) + ' <span class="bag-title-count">(' + count + ')</span></h2>';

    if (!count) {
      return (
        html +
        '<div class="empty-bag">' + icon('bag') + '<p>' + esc(t.bagEmpty) + '</p>' +
        '<button type="button" class="button primary keep-shopping" id="keep-shopping" data-pdp-close>' + esc(t.bagContinue) + '</button></div>'
      );
    }

    html += '<div class="bag-items">';
    items.forEach(function (item) {
      var title = String(item.product_title || item.title || '').replace(/iphone/gi, 'iPhone');
      var variantTitle = item.product_has_only_default_variant ? '' : String(item.variant_title || '').replace(/iphone/gi, 'iPhone');
      var name = title + (variantTitle ? ', ' + variantTitle : '');
      var image = item.featured_image && item.featured_image.url ? item.featured_image.url : item.image;
      var key = esc(item.key);
      var quantity = Number(item.quantity) || 0;
      var linePrice = self.money(item.final_line_price);
      var original = Number(item.original_line_price) > Number(item.final_line_price) ? '<s>' + esc(self.money(item.original_line_price)) + '</s> ' : '';
      html +=
        '<div class="bag-item">' +
        '<a class="bag-item-media" href="' + esc(item.url) + '" tabindex="-1" aria-hidden="true">' +
        (image ? '<img src="' + esc(withWidth(image, 180)) + '" alt="" width="72" height="86" loading="lazy">' : '') +
        '</a>' +
        '<div><strong><a href="' + esc(item.url) + '">' + esc(title) + '</a></strong>' +
        (variantTitle ? '<p>' + esc(variantTitle) + '</p>' : '') +
        '<div class="bag-item-actions"><div class="bag-stepper">' +
        '<button type="button" data-pdp-bag-step="-1" data-key="' + key + '" aria-label="' + esc(fill(t.bagDecrease, { title: name })) + '"' + (quantity <= 1 ? ' disabled' : '') + '>−</button>' +
        '<span>' + quantity + '</span>' +
        '<button type="button" data-pdp-bag-step="1" data-key="' + key + '" aria-label="' + esc(fill(t.bagIncrease, { title: name })) + '">+</button>' +
        '</div>' +
        '<button type="button" class="bag-remove" data-pdp-bag-remove data-key="' + key + '" aria-label="' + esc(fill(t.bagRemoveLabel, { title: name })) + '">' + esc(t.bagRemove) + '</button>' +
        '</div></div>' +
        '<span class="bag-line-price">' + original + esc(linePrice) + '</span>' +
        '</div>';
    });
    html += '</div>';

    html += '<p class="bag-error" role="alert" data-pdp="bag-error"></p>';
    if (this.data.showOffer) {
      var offer = count >= 2 ? t.bagOfferMany : t.bagOfferOne;
      if (offer) html += '<div class="bag-discount">' + esc(offer) + '</div>';
    }
    var subtotal = this.money(cart.items_subtotal_price != null ? cart.items_subtotal_price : cart.total_price);
    if (this.data.showCurrency && cart.currency) subtotal += ' ' + cart.currency;
    html += '<div class="bag-total"><span>' + esc(t.bagSubtotal) + '</span><b>' + esc(subtotal) + '</b></div>';
    html +=
      '<form class="bag-checkout" action="' + esc(cartUrl) + '" method="post">' +
      '<button type="submit" name="checkout" class="button primary">' + esc(t.bagCheckout) + '</button></form>';
    if (t.bagFineprint) html += '<p class="fineprint">' + esc(t.bagFineprint) + '</p>';
    html +=
      '<div class="bag-links"><a class="text-link" href="' + esc(cartUrl) + '">' + esc(t.bagView) + '</a>' +
      '<button type="button" class="text-link keep-shopping" id="keep-shopping" data-pdp-close>' + esc(t.bagContinue) + '</button></div>';
    return html;
  };

  Pdp.prototype.changeLine = function (key, quantity, focusSelector) {
    var self = this;
    var box = this.el.dialogContent;
    if (!box || this.bagBusy) return;
    this.bagBusy = true;
    box.querySelectorAll('[data-pdp-bag-step], [data-pdp-bag-remove]').forEach(function (button) {
      button.disabled = true;
    });
    requestJSON((this.routes.cartChange || '/cart/change') + '.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (cart) {
        self.bagBusy = false;
        refreshHeaderCart();
        if (self.dialogType() !== 'bag') return;
        box.innerHTML = self.bagHTML(cart, false);
        var target = focusSelector ? box.querySelector(focusSelector) : null;
        if (!target || target.disabled) target = box.querySelector('[data-pdp-bag-remove], #keep-shopping');
        focusEl(target);
      })
      .catch(function (error) {
        self.bagBusy = false;
        box.querySelectorAll('[data-pdp-bag-step], [data-pdp-bag-remove]').forEach(function (button) {
          button.disabled = button.getAttribute('data-pdp-bag-step') === '-1' && button.nextElementSibling && button.nextElementSibling.textContent === '1';
        });
        setText(box.querySelector('[data-pdp="bag-error"]'), (error && error.userMessage) || self.t.bagError);
      });
  };

  /* ----------------------------------------------------------------- share */

  Pdp.prototype.shareUrl = function () {
    var url = window.location.origin + (this.data.url || window.location.pathname);
    return this.variant && !this.modelMissing() ? url + '?variant=' + this.variant.id : url;
  };

  Pdp.prototype.share = function (opener) {
    var self = this;
    var url = this.shareUrl();
    if (navigator.share) {
      navigator.share({ title: this.data.title || doc.title, url: url }).catch(function (error) {
        if (error && error.name === 'AbortError') return;
        self.openShareDialog(url, opener);
      });
      return;
    }
    this.openShareDialog(url, opener);
  };

  Pdp.prototype.openShareDialog = function (url, opener) {
    var content = this.template('share');
    if (!content) return;
    var input = content.querySelector('[data-pdp="share-url"]');
    if (input) input.value = url;
    var email = content.querySelector('[data-pdp="share-email"]');
    if (email) email.href = 'mailto:?subject=' + encodeURIComponent(this.t.shareEmailSubject || '') + '&body=' + encodeURIComponent(url);
    this.openDialog('share-dialog', content, opener);
  };

  Pdp.prototype.copyLink = function (button) {
    var self = this;
    var box = this.el.dialogContent;
    var input = box ? box.querySelector('[data-pdp="share-url"]') : null;
    var url = input ? input.value : this.shareUrl();
    var done = function () {
      setText(button, self.t.shareCopied);
      self.toast(self.t.shareCopied);
    };
    var fallback = function () {
      if (input) {
        input.focus();
        input.select();
      }
      var copied = false;
      try {
        copied = doc.execCommand('copy');
      } catch (error) {
        copied = false;
      }
      if (copied) done();
      else setText(button, self.t.shareCopyFallback);
    };
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard.writeText(url).then(done, fallback);
    } else {
      fallback();
    }
  };

  /* ------------------------------------------------------------ info tabs */

  Pdp.prototype.openPanel = function (name) {
    var event = new CustomEvent('atbt:pdp:open-panel', { bubbles: true, cancelable: true, detail: { panel: name } });
    if (!doc.dispatchEvent(event)) return;
    var tab = doc.getElementById('tab-' + name) || doc.querySelector('[role="tab"][data-panel="' + name + '"]');
    var behavior = reduced() ? 'auto' : 'smooth';
    if (!tab) {
      var target = doc.getElementById(name);
      if (target) target.scrollIntoView({ behavior: behavior, block: 'start' });
      return;
    }
    if (tab.getAttribute('aria-selected') !== 'true') tab.click();
    var region = tab.closest('section') || tab.parentElement || tab;
    region.scrollIntoView({ behavior: behavior, block: 'start' });
    focusEl(tab);
  };

  /* ---------------------------------------------------------- sticky bar */

  Pdp.prototype.observeSticky = function () {
    var sticky = this.el.sticky;
    var target = this.el.buyControls;
    if (!sticky || !target || !('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver(
      function (entries) {
        var entry = entries[entries.length - 1];
        var show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('visible', show);
        if (show) sticky.removeAttribute('inert');
        else sticky.setAttribute('inert', '');
      },
      { threshold: 0 }
    );
    this.observer.observe(target);
  };

  /* --------------------------------------------------------------- events */

  Pdp.prototype.bind = function () {
    var self = this;
    var root = this.root;

    root.addEventListener('pointerdown', function (event) {
      self.pointerTarget = event.target;
    });

    root.addEventListener('click', function (event) {
      self.onClick(event);
    });

    root.addEventListener('change', function (event) {
      var target = event.target;
      if (!isEl(target)) return;
      if (target.matches('select[data-pdp-select]')) {
        var group = self.groupFor(Number(target.closest('[data-pdp-option]').getAttribute('data-pdp-option')));
        if (group) self.selectValue(group.index, target.value);
      } else if (target === self.el.qty) {
        self.setQty(target.value);
      }
    });

    root.addEventListener('input', function (event) {
      if (event.target === self.el.modelSearch) self.renderModelResults();
      else if (event.target === self.el.qty && self.el.qty.value !== '') self.setQty(self.el.qty.value);
    });

    if (this.el.form) {
      this.el.form.addEventListener('submit', function (event) {
        event.preventDefault();
        self.addToBag('main');
      });
    }

    root.addEventListener('keydown', function (event) {
      self.onKeydown(event);
    });

    if (this.el.dialog) {
      this.el.dialog.addEventListener('close', function () {
        self.onDialogClose();
      });
    }
    if (this.el.modelDialog) {
      this.el.modelDialog.addEventListener('close', function () {
        self.onModelDialogClose();
      });
    }

    var photo = this.el.mainPhoto;
    if (photo) {
      photo.addEventListener(
        'touchstart',
        function (event) {
          var touch = event.changedTouches[0];
          self.touchStart = [touch.clientX, touch.clientY];
        },
        { passive: true }
      );
      photo.addEventListener(
        'touchend',
        function (event) {
          if (!self.touchStart) return;
          var touch = event.changedTouches[0];
          var dx = touch.clientX - self.touchStart[0];
          var dy = touch.clientY - self.touchStart[1];
          self.touchStart = null;
          if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
            self.stepMedia(dx < 0 ? 1 : -1);
            self.suppressPhotoClick = Date.now() + 400;
          }
        },
        { passive: true }
      );
    }
  };

  Pdp.prototype.onClick = function (event) {
    var target = event.target;
    if (!isEl(target)) return;
    var t = this;
    var pressedOn = this.pointerTarget;
    this.pointerTarget = null;

    // Backdrop clicks close the dialogs.
    if (target === this.el.dialog || target === this.el.modelDialog) {
      if ((pressedOn === null || pressedOn === target) && outsideRect(target, event)) closeModal(target);
      return;
    }

    var closer = target.closest('[data-pdp-close]');
    if (closer) {
      closeModal(closer.closest('dialog'));
      return;
    }

    var valueButton = target.closest('[data-pdp-value]');
    if (valueButton) {
      var group = this.groupFor(Number(valueButton.closest('[data-pdp-option]').getAttribute('data-pdp-option')));
      if (group) this.selectValue(group.index, valueButton.getAttribute('data-pdp-value'));
      return;
    }

    var modelResult = target.closest('[data-pdp-model]');
    if (modelResult) {
      if (!modelResult.disabled) this.chooseModel(modelResult.getAttribute('data-pdp-model'));
      return;
    }

    if (target.closest('[data-pdp="model-trigger"]')) {
      this.openModelDialog(this.el.modelTrigger);
      return;
    }

    var thumb = target.closest('[data-pdp="thumbs"] [data-media]');
    if (thumb) {
      this.showMedia(Number(thumb.getAttribute('data-media')), true);
      return;
    }

    if (target.closest('[data-pdp="zoom"]')) {
      this.openZoom(this.mediaIndex, this.el.zoom);
      return;
    }

    if (this.el.mainPhoto && target === this.el.mainPhoto) {
      if (Date.now() > this.suppressPhotoClick) this.openZoom(this.mediaIndex, this.el.hero);
      return;
    }

    var opener = target.closest('[data-pdp-open]');
    if (opener) {
      this.open(opener.getAttribute('data-pdp-open'), opener);
      return;
    }

    var panel = target.closest('[data-pdp-open-panel]');
    if (panel) {
      this.openPanel(panel.getAttribute('data-pdp-open-panel'));
      return;
    }

    var step = target.closest('[data-pdp-bag-step]');
    if (step) {
      var line = step.parentElement ? step.parentElement.querySelector('span') : null;
      var current = line ? Number(line.textContent) || 1 : 1;
      var delta = Number(step.getAttribute('data-pdp-bag-step'));
      var key = step.getAttribute('data-key');
      this.changeLine(key, Math.max(0, current + delta), '[data-key="' + key + '"][data-pdp-bag-step="' + delta + '"]');
      return;
    }

    var remove = target.closest('[data-pdp-bag-remove]');
    if (remove) {
      this.changeLine(remove.getAttribute('data-key'), 0, null);
      return;
    }

    var action = target.closest('[data-pdp-action]');
    if (action) {
      this.onAction(action.getAttribute('data-pdp-action'), action);
      return;
    }

    if (target.closest('[data-pdp="qty-minus"]')) {
      this.setQty(this.qty - 1);
      return;
    }
    if (target.closest('[data-pdp="qty-plus"]')) {
      this.setQty(this.qty + 1);
      return;
    }
    if (target.closest('[data-pdp="sticky-add"]')) {
      t.addToBag('sticky');
    }
  };

  Pdp.prototype.onAction = function (name, button) {
    var self = this;
    switch (name) {
      case 'choose-model':
        this.closeDialog(false);
        window.setTimeout(function () {
          self.promptModel(self.el.modelTrigger, false);
        }, 0);
        break;
      case 'choose-size':
        this.closeDialog(false);
        this.focusSizeOption();
        break;
      case 'copy-link':
        this.copyLink(button);
        break;
      case 'zoom-prev':
        this.stepZoom(-1);
        break;
      case 'zoom-next':
        this.stepZoom(1);
        break;
      default:
        break;
    }
  };

  Pdp.prototype.focusSizeOption = function () {
    var trigger = this.root.querySelector('[data-pdp-open="size-guide"]');
    var picker = trigger ? trigger.closest('[data-pdp-option]') : null;
    if (!picker) return;
    picker.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'center' });
    focusEl(picker.querySelector('[aria-pressed="true"]') || picker.querySelector('select, button'));
  };

  Pdp.prototype.onKeydown = function (event) {
    var target = event.target;
    if (!isEl(target)) return;
    var key = event.key;

    if (this.dialogType() === 'zoom' && (key === 'ArrowLeft' || key === 'ArrowRight') && this.el.dialog.contains(target)) {
      event.preventDefault();
      this.stepZoom(key === 'ArrowRight' ? 1 : -1);
      return;
    }

    if (target === this.el.hero) {
      if (key === 'ArrowLeft' || key === 'ArrowRight') {
        event.preventDefault();
        this.stepMedia(key === 'ArrowLeft' ? -1 : 1);
      } else if ((key === 'Enter' || key === ' ') && !this.isVideo(this.media[this.mediaIndex])) {
        event.preventDefault();
        this.openZoom(this.mediaIndex, this.el.hero);
      }
      return;
    }

    if (this.el.thumbs && this.el.thumbs.contains(target) && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].indexOf(key) > -1) {
      var buttons = Array.prototype.slice.call(this.el.thumbs.querySelectorAll('[data-media]'));
      var index = buttons.indexOf(target.closest('[data-media]'));
      if (index < 0) return;
      event.preventDefault();
      var next = key === 'Home' ? 0 : key === 'End' ? buttons.length - 1 : (index + (key === 'ArrowRight' || key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
      buttons[next].click();
      focusEl(buttons[next]);
      return;
    }

    if (this.el.modelDialog && this.el.modelDialog.contains(target) && (key === 'ArrowDown' || key === 'ArrowUp')) {
      var results = Array.prototype.slice.call(this.el.modelResults.querySelectorAll('.model-result:not([disabled])'));
      if (!results.length) return;
      event.preventDefault();
      var position = results.indexOf(target);
      if (target === this.el.modelSearch) position = key === 'ArrowDown' ? 0 : results.length - 1;
      else position = (position + (key === 'ArrowDown' ? 1 : -1) + results.length) % results.length;
      focusEl(results[position]);
    }
  };

  Pdp.prototype.destroy = function () {
    if (this.observer) this.observer.disconnect();
    if (this.el.dialog && this.el.dialog.open) closeModal(this.el.dialog);
    if (this.el.modelDialog && this.el.modelDialog.open) closeModal(this.el.modelDialog);
    if (this.el.dialog) unlockScroll(this.el.dialog);
    if (this.el.modelDialog) unlockScroll(this.el.modelDialog);
    window.clearTimeout(this.toastTimer);
    var index = instances.indexOf(this);
    if (index > -1) instances.splice(index, 1);
    this.root.__atbtPdp = null;
  };

  /* ------------------------------------------------------------- bootstrap */

  function boot(scope) {
    var roots = [];
    if (isEl(scope) && scope.matches('[data-atbt-pdp]')) roots.push(scope);
    (scope || doc).querySelectorAll('[data-atbt-pdp]').forEach(function (root) {
      roots.push(root);
    });
    roots.forEach(function (root) {
      if (root.__atbtPdp) return;
      try {
        new Pdp(root);
      } catch (error) {
        if (window.console) window.console.error('ATBT product page:', error);
      }
    });
  }

  function primary() {
    return instances[0] || null;
  }

  // Hooks used by other product sections (info tabs, details image, footer links).
  doc.addEventListener('click', function (event) {
    var target = event.target;
    if (!isEl(target) || target.closest('[data-atbt-pdp]')) return;
    var opener = target.closest('[data-dialog="fit"], [data-pdp-open], [data-photo], [data-photo-id]');
    var instance = primary();
    if (!opener || !instance) return;
    event.preventDefault();
    if (opener.hasAttribute('data-pdp-open')) instance.open(opener.getAttribute('data-pdp-open'), opener);
    else if (opener.matches('[data-dialog="fit"]')) instance.open('fit', opener);
    else if (opener.hasAttribute('data-photo-id')) instance.openZoom(instance.mediaIndexById(opener.getAttribute('data-photo-id')), opener);
    else instance.openZoom(Number(opener.getAttribute('data-photo')), opener);
  });

  doc.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });

  doc.addEventListener('shopify:section:unload', function (event) {
    var scope = event.target;
    instances.slice().forEach(function (instance) {
      if (isEl(scope) && scope.contains(instance.root)) instance.destroy();
    });
  });

  window.AtbtPdp = { boot: boot, instances: instances };
  window.ATBT = window.ATBT || {};
  window.ATBT.pdp = {
    open: function (name, opener) {
      var instance = primary();
      if (instance) instance.open(name, opener);
    },
    openPanel: function (name) {
      var instance = primary();
      if (instance) instance.openPanel(name);
    },
    openZoom: function (index) {
      var instance = primary();
      if (instance) instance.openZoom(index);
    },
    openBag: function () {
      var instance = primary();
      if (instance) instance.openBag(null, false);
    }
  };

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', function () {
      boot(doc);
    });
  } else {
    boot(doc);
  }
})();
